import * as Sentry from '@sentry/react';
import { getFriendlyCloudinaryUploadErrorMessage } from 'cloudinary';
import { CloudinaryUploadError, CropImageError, cropAndUploadImage } from 'components/Ui-V3/ImageEditor/cropAndUpload';
import { useBizlySnackbar } from 'hooks/useBizlySnackbar';
import { useCallback, useState } from 'react';
import { Area } from 'react-easy-crop';

type UseImageEditorUploadProps = {
    onImageChange?: (image: { url: string; title?: string }) => void;
    /** Overrides the generic 40MB Cloudinary-preset limit for callers with a smaller limit of their own. */
    maxFileBytes?: number;
};

export const useImageEditorUpload = ({ onImageChange, maxFileBytes }: UseImageEditorUploadProps) => {
    const { bizlyOSErrorSnackbar } = useBizlySnackbar();
    const [showEditor, setShowEditor] = useState<boolean>(false);
    const [editorImage, setEditorImage] = useState<{ url: string; title: string }>({ url: '', title: '' });
    const [rotation, setRotation] = useState<number>(0);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const resetEditor = useCallback(() => {
        setRotation(0);
        setCroppedAreaPixels(null);
        setEditorImage({ url: '', title: '' });
    }, []);

    const openEditor = useCallback((image: { url: string; title: string }) => {
        setEditorImage(image);
        setShowEditor(true);
    }, []);

    const closeEditor = useCallback(() => {
        setShowEditor(false);
        resetEditor();
    }, [resetEditor]);

    const uploadCropped = useCallback(
        async (image: { url: string; title: string }) => {
            if (!croppedAreaPixels) {
                bizlyOSErrorSnackbar('Image is still loading. Please wait a moment and try again.');
                return null;
            }

            try {
                return await cropAndUploadImage(image, croppedAreaPixels, rotation);
            } catch (e) {
                console.error(e);
                const action =
                    e instanceof CropImageError
                        ? 'getCroppedImg'
                        : e instanceof CloudinaryUploadError
                          ? 'uploadToCloudinary'
                          : 'uploadCropped';
                Sentry.captureException(e, {
                    tags: { component: 'useImageEditorUpload', action },
                    extra: { imageUrl: image?.url, rotation, croppedAreaPixels },
                });
                bizlyOSErrorSnackbar(
                    getFriendlyCloudinaryUploadErrorMessage(e, maxFileBytes) ??
                        'Failed to process the image. Please try again.'
                );
                throw e;
            }
        },
        [croppedAreaPixels, rotation, bizlyOSErrorSnackbar, maxFileBytes]
    );

    const confirmAndUpload = useCallback(async () => {
        if (!croppedAreaPixels) {
            bizlyOSErrorSnackbar('Image is still loading. Please wait a moment and try again.');
            return;
        }

        try {
            setIsLoading(true);
            const result = await uploadCropped(editorImage);

            if (result) {
                onImageChange?.(result);
                closeEditor();
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    }, [editorImage, uploadCropped, onImageChange, closeEditor, croppedAreaPixels, bizlyOSErrorSnackbar]);

    return {
        showEditor,
        editorImage,
        rotation,
        isLoading,
        isCropReady: !!croppedAreaPixels,
        openEditor,
        closeEditor,
        confirmAndUpload,
        setRotation,
        setCroppedAreaPixels,
    };
};
